/**
 * The firm's own app ranking, for the Integrations page (legal apps delivery 2b; Miles, 2026-09-28, ruling
 * 6: "the highest recommended applications will come towards the top both in the unified onboarding and in
 * the integrations page depending on information and context about the firm").
 *
 * getOnboardingRecommendations (functions/src/onboardingRecommendations.ts) caches what its model ranked for
 * this person's firm at users/{uid}/onboarding/recommendations, a document its owner may read (the
 * users/{uid} fallback rule). This reads that answer; it ranks nothing itself. Only a FIRM ranking counts
 * (`mode: 'firm'`): the general defaults are adoption order, which the page's own sort already is. A read
 * that fails, a missing document or a malformed one is "no ranking", and the page keeps its own order.
 *
 * One read per person per page load (a failed one is forgotten, so the next load asks again).
 */
import { useEffect, useState } from 'react';
import { getApp } from 'firebase/app';
import { doc, getDoc, getFirestore } from 'firebase/firestore';

/** At most this many ranked apps (the ranking's own catalog size). */
export const FIRM_RANKING_MAX = 12;

/** The ranked keys a cached answer carries, best first: its catalog picks, else its connect rows. */
export function rankingFromCache(data: unknown): string[] {
  if (!data || typeof data !== 'object') return [];
  const recs = (data as { recommendations?: unknown }).recommendations;
  if (!recs || typeof recs !== 'object') return [];
  const r = recs as { mode?: unknown; catalogRecommendations?: unknown; recommendedIntegrations?: unknown };
  if (r.mode !== 'firm') return [];
  const list = Array.isArray(r.catalogRecommendations) && r.catalogRecommendations.length > 0
    ? r.catalogRecommendations
    : Array.isArray(r.recommendedIntegrations) ? r.recommendedIntegrations : [];
  const out: string[] = [];
  for (const v of list) {
    if (typeof v !== 'string') continue;
    const key = v.trim().toLowerCase();
    if (!/^[a-z0-9_]{2,64}$/.test(key) || out.includes(key)) continue;
    out.push(key);
    if (out.length >= FIRM_RANKING_MAX) break;
  }
  return out;
}

const reads = new Map<string, Promise<string[]>>();

/** The firm ranking for this person, read once per page load. Never throws. */
export function readFirmRanking(uid: string): Promise<string[]> {
  const cached = reads.get(uid);
  if (cached) return cached;
  // An async function, so a throw before the read (no app initialised) is a failed read, never a crash.
  const read = (async () => {
    const snap = await getDoc(doc(getFirestore(getApp()), 'users', uid, 'onboarding', 'recommendations'));
    return snap.exists() ? rankingFromCache(snap.data()) : [];
  })().catch(() => {
    reads.delete(uid);
    return [] as string[];
  });
  reads.set(uid, read);
  return read;
}

/** The firm ranking for the signed-in person, or [] until (and unless) it arrives. */
export function useFirmRanking(uid: string | null | undefined): string[] {
  const [ranking, setRanking] = useState<string[]>([]);
  useEffect(() => {
    if (!uid) {
      setRanking([]);
      return undefined;
    }
    let alive = true;
    void readFirmRanking(uid).then((list) => {
      if (alive) setRanking(list);
    });
    return () => {
      alive = false;
    };
  }, [uid]);
  return ranking;
}

/**
 * A sort that puts the firm's ranked apps first, in the ranking's order, and leaves every other app in the
 * page's own order (`then`). Nothing is removed and nothing unranked moves.
 */
export function rankedFirst<T>(ranking: readonly string[], keyOf: (item: T) => string, then: (a: T, b: T) => number): (a: T, b: T) => number {
  const rank = new Map(ranking.map((k, i) => [k, i] as [string, number]));
  return (a, b) => {
    const ra = rank.get(keyOf(a).toLowerCase());
    const rb = rank.get(keyOf(b).toLowerCase());
    if (ra !== undefined && rb !== undefined) return ra - rb;
    if (ra !== undefined) return -1;
    if (rb !== undefined) return 1;
    return then(a, b);
  };
}

/** Tests only: forget the reads. */
export function resetFirmRankingForTests(): void {
  reads.clear();
}
